import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Loader2, Pause, Play, RotateCw, Save, Trash2, Wrench } from 'lucide-react';
import type { ScraperRecord, RunsPerDay } from '@/types';
import { RUNS_PER_DAY_PRESETS } from '@/types';
import {
  setScraperEnabled,
  setScraperSchedule,
  runScraperNow,
  deleteScraper,
  getScraperRuntimeConfig,
  updateScraperRuntimeConfig,
} from '@/api/observe';
import { relativeTime, pct, compactNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

const STATUS_TONE: Record<string, string> = {
  ok:      'bg-emerald-500',
  blocked: 'bg-red-500',
  error:   'bg-orange-500',
  never:   'bg-slate-500',
};

const EXECUTOR_LABEL: Record<string, string> = {
  cf_workers:    'CF Workers',
  real_browsers: 'Real Browsers',
  docker:        'Docker',
  unknown:       'Unknown',
};

export default function ScraperCard({ scraper }: { scraper: ScraperRecord }) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['scrapers'] });

  const toggleMut = useMutation({
    mutationFn: (next: boolean) => setScraperEnabled(scraper.id, next),
    onSuccess: invalidate,
  });
  const scheduleMut = useMutation({
    mutationFn: (rpd: RunsPerDay) => setScraperSchedule(scraper.id, rpd),
    onSuccess: invalidate,
  });
  const runNowMut = useMutation({
    mutationFn: () => runScraperNow(scraper.id),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteScraper(scraper.id),
    onSuccess: invalidate,
  });

  // Hard-delete: scraper row goes away from the registry. The actual
  // cron entry / running container isn't touched - if the scraper
  // still has a cron schedule it will heartbeat back in on its next
  // tick and re-create the row. We surface that nuance in the prompt
  // so users don't expect a magical kill switch.
  const onDelete = () => {
    const ok = window.confirm(
      `Delete "${scraper.display_name}" (${scraper.id}) from the registry?\n\n` +
      `This removes the dashboard row. If the scraper is still running on cron ` +
      `it will re-register on its next heartbeat — disable its cron entry first ` +
      `if you want it gone for good.`
    );
    if (ok) deleteMut.mutate();
  };

  const statusKey = scraper.last_run_status ?? 'never';

  return (
    <div className={cn(
      'rounded-lg border bg-slate-900/40 p-4 transition',
      scraper.enabled ? 'border-slate-800' : 'border-slate-800/50 opacity-60',
    )}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('dot', STATUS_TONE[statusKey])} />
            <span className="font-medium truncate">{scraper.display_name}</span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
            <span className="font-mono">{scraper.id}</span>
            <span>·</span>
            <span>{EXECUTOR_LABEL[scraper.executor] ?? scraper.executor}</span>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Link
            to="/scrapers/remediate"
            search={{ scraperId: scraper.id }}
            className={cn(
              'tap-target rounded hover:bg-slate-800 text-slate-400 hover:text-white',
              scraper.last_run_status === 'blocked' && 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 ring-1 ring-amber-500/30',
            )}
            title="Remediate / Switch executor"
            aria-label="Remediate / Switch executor"
          >
            <Wrench className="w-4 h-4" />
          </Link>
          <button
            onClick={() => runNowMut.mutate()}
            disabled={runNowMut.isPending}
            className="tap-target rounded hover:bg-slate-800 text-slate-400 hover:text-white disabled:opacity-50"
            aria-label="Run now"
            title="Run now"
          >
            <RotateCw className={cn('w-4 h-4', runNowMut.isPending && 'animate-spin')} />
          </button>
          <button
            onClick={() => toggleMut.mutate(!scraper.enabled)}
            disabled={toggleMut.isPending}
            className={cn(
              'tap-target rounded hover:bg-slate-800 disabled:opacity-50',
              scraper.enabled ? 'text-emerald-400' : 'text-slate-500',
            )}
            aria-label={scraper.enabled ? 'Pause' : 'Resume'}
            title={scraper.enabled ? 'Pause' : 'Resume'}
          >
            {scraper.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={onDelete}
            disabled={deleteMut.isPending}
            className="tap-target rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 disabled:opacity-50"
            aria-label="Delete scraper from registry"
            title="Delete scraper from registry"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-xs mb-3">
        <Stat label="Last run"  value={relativeTime(scraper.last_run_at)} />
        <Stat label="Last run" value={compactNumber(scraper.saved_pages_last_run)} />
        <Stat label="Bot rate"  value={pct(scraper.bot_rate_24h, 0)} tone={(scraper.bot_rate_24h ?? 0) > 0.4 ? 'bad' : undefined} />
      </div>

      {/* Frequency picker */}
      <div>
        <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
          <span>Runs / day</span>
          {scheduleMut.isPending && <span className="text-slate-400">saving…</span>}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {RUNS_PER_DAY_PRESETS.map(rpd => (
            <button
              key={rpd}
              onClick={() => scheduleMut.mutate(rpd)}
              className={cn(
                'h-9 sm:h-7 rounded text-xs font-mono transition',
                scraper.runs_per_day === rpd
                  ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40'
                  : 'bg-slate-800/60 text-slate-400 hover:bg-slate-800 hover:text-white',
              )}
              title={`${rpd}× per day (~every ${Math.round(24 * 60 / rpd)} min)`}
            >
              {rpd}
            </button>
          ))}
        </div>
      </div>

      {/* Runtime config — keywords + max_pages override */}
      <RuntimeConfigEditor scraperId={scraper.id} />

      {/* Last alert */}
      {scraper.last_alert && (
        <div className="mt-3 pt-3 border-t border-slate-800/60 text-xs">
          <div className="text-slate-500 mb-1">Last alert</div>
          <div className="truncate text-slate-300">{scraper.last_alert.message}</div>
          <div className="text-slate-500 mt-0.5">{relativeTime(scraper.last_alert.ts)}</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="bg-slate-900/60 rounded px-2 py-1.5">
      <div className="text-slate-500 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={cn(
        'font-mono mt-0.5',
        tone === 'bad' ? 'text-red-300' : 'text-slate-200',
      )}>
        {value}
      </div>
    </div>
  );
}

/** Collapsible editor for the per-scraper search_terms + max_pages +
 *  base_url override stored on `scrapers_registry`. Lazy-fetches the
 *  config only when expanded so the cards list stays cheap.
 *
 *  Inputs are pre-filled with the *current effective values* (override
 *  if set, else the scraper-reported default, else env-var fallback)
 *  so operators see exactly what's running and can edit in place.
 *  Clearing an input entirely + Save sends `null` to the backend,
 *  which `$unset`s the override and reverts the scraper to its
 *  baked-in default on the next run. */
function RuntimeConfigEditor({ scraperId }: { scraperId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // null = not-yet-hydrated; '' = operator cleared the input.
  const [terms, setTerms]       = useState<string | null>(null);
  const [maxPages, setMaxPages] = useState<string | null>(null);
  const [baseUrl, setBaseUrl]   = useState<string | null>(null);

  const cfgQ = useQuery({
    queryKey: ['scraper-runtime-config', scraperId],
    queryFn: () => getScraperRuntimeConfig(scraperId),
    enabled: open,
    refetchOnWindowFocus: false,
  });

  const cfg = cfgQ.data;
  // Hydrate the editable fields from the GET when it first lands.
  // null in local state means "not hydrated yet" — only swap to the
  // server value on the initial load, so re-renders don't stomp on
  // in-progress edits.
  if (cfg && terms === null && maxPages === null && baseUrl === null) {
    setTerms(cfg.search_terms.join(', '));
    setMaxPages(cfg.max_pages != null ? String(cfg.max_pages) : '');
    setBaseUrl(cfg.base_url ?? '');
  }

  const saveMut = useMutation({
    mutationFn: () => {
      const parsedTerms = (terms ?? '').split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
      const trimmedMax  = (maxPages ?? '').trim();
      const trimmedBase = (baseUrl ?? '').trim();
      return updateScraperRuntimeConfig(scraperId, {
        // Empty input = clear override, fall back to the scraper's
        // default. Non-empty = persist as an override.
        search_terms: parsedTerms.length === 0 ? null : parsedTerms,
        max_pages:    trimmedMax  === '' ? null : Number(trimmedMax),
        base_url:     trimmedBase === '' ? null : trimmedBase,
      });
    },
    onSuccess: (resp) => {
      qc.invalidateQueries({ queryKey: ['scrapers'] });
      qc.setQueryData(['scraper-runtime-config', scraperId], { ok: true, ...resp.effective });
      // Re-hydrate from the server's effective response so cleared
      // overrides fall back to the scraper's default in the UI.
      setTerms(resp.effective.search_terms.join(', '));
      setMaxPages(resp.effective.max_pages != null ? String(resp.effective.max_pages) : '');
      setBaseUrl(resp.effective.base_url ?? '');
    },
  });

  // Header summary — always shows effective values (override if set,
  // else scraper default, else dash). Also surfaces the base-URL host
  // so operators can spot when a scraper's been pointed at the wrong
  // storefront at a glance.
  const effectiveHost = cfg?.base_url ? safeHost(cfg.base_url) : null;
  const headerSummary = cfg
    ? `${cfg.search_terms.length} term${cfg.search_terms.length === 1 ? '' : 's'} · max ${cfg.max_pages ?? '—'}/term${effectiveHost ? ` · ${effectiveHost}` : ''}`
    : null;

  const dirty = cfg
    ? terms    !== cfg.search_terms.join(', ')
      || maxPages !== (cfg.max_pages != null ? String(cfg.max_pages) : '')
      || baseUrl  !== (cfg.base_url ?? '')
    : false;

  const envHintFor = (k: 'search_terms' | 'max_pages' | 'base_url') => {
    const upper = scraperId.toUpperCase();
    if (k === 'search_terms') return `${upper}_SEARCH_TERMS`;
    if (k === 'max_pages')    return `${upper}_MAX_PAGES_PER_TERM`;
    return `${upper}_BASE_URL`;
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-800/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-200 transition"
      >
        <span className="flex items-center gap-1.5">
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          Keywords, pages &amp; base URL
        </span>
        {headerSummary && (
          <span className="text-[10px] text-slate-500 font-mono truncate max-w-[60%]" title={headerSummary}>
            {headerSummary}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          {cfgQ.isLoading && (
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading runtime config…
            </div>
          )}
          {cfgQ.error && (
            <div className="text-xs text-red-300">Failed to load: {(cfgQ.error as Error).message}</div>
          )}
          {cfg && (
            <>
              <FieldBlock
                label="Search terms (comma-separated)"
                source={cfg.source_search_terms}
                envKey={envHintFor('search_terms')}
              >
                <textarea
                  value={terms ?? ''}
                  onChange={(e) => setTerms(e.target.value)}
                  rows={4}
                  className="input w-full font-mono text-[11px] resize-y"
                  placeholder="iphone 15, ps5, xbox series x"
                  spellCheck={false}
                />
              </FieldBlock>

              <FieldBlock
                label="Max pages per term"
                source={cfg.source_max_pages}
                envKey={envHintFor('max_pages')}
              >
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={maxPages ?? ''}
                  onChange={(e) => setMaxPages(e.target.value)}
                  className="input w-full text-xs"
                  placeholder="3"
                />
              </FieldBlock>

              <FieldBlock
                label="Base URL (search-URL template)"
                source={cfg.source_base_url}
                envKey={envHintFor('base_url')}
              >
                <input
                  type="url"
                  value={baseUrl ?? ''}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  className="input w-full font-mono text-[11px]"
                  placeholder="https://www.amazon.co.uk/s?k={term}"
                  spellCheck={false}
                />
              </FieldBlock>

              {saveMut.error && (
                <div className="text-[11px] text-red-300">{(saveMut.error as Error).message}</div>
              )}

              <div className="flex items-center justify-between gap-2 pt-1">
                <span className="text-[10px] text-slate-500">
                  {cfg.runtime_config_updated_at
                    ? `Last edited ${relativeTime(cfg.runtime_config_updated_at)}`
                    : 'Clear an input + save to revert to the scraper default'}
                </span>
                <button
                  type="button"
                  onClick={() => saveMut.mutate()}
                  disabled={!dirty || saveMut.isPending}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded transition',
                    dirty
                      ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25'
                      : 'bg-slate-800 border border-slate-700 text-slate-500',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                  )}
                >
                  {saveMut.isPending
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                    : <><Save className="w-3 h-3" /> Save override</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Per-field wrapper: label row with source-state badge + the input
 *  itself + a small env-fallback hint underneath. Replaces the
 *  previous standalone `SourceBadge` so the three editor rows share
 *  one consistent layout. */
function FieldBlock({
  label,
  source,
  envKey,
  children,
}: {
  label: string;
  source: 'registry' | 'default' | 'static' | 'env' | 'none';
  envKey: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
        <span>{label}</span>
        <StateBadge source={source} />
      </div>
      {children}
      <p className="text-[10px] text-slate-500 mt-0.5">
        Empty = clear override, falls back to scraper default (<code>{envKey}</code>).
      </p>
    </div>
  );
}

/** Compact pill that tells the operator whether the value next to it
 *  is the operator override or the scraper's baked-in default. We
 *  collapse the three "non-override" states ('default', 'env', 'none')
 *  into a single muted label because, from the operator's POV, they
 *  all mean "this is what the scraper would run absent any edit". */
function StateBadge({ source }: { source: 'registry' | 'default' | 'static' | 'env' | 'none' }) {
  if (source === 'registry') {
    return (
      <span className="text-[9px] px-1.5 py-px rounded bg-sky-500/15 text-sky-200 uppercase tracking-wide ring-1 ring-sky-500/30">
        Override active
      </span>
    );
  }
  if (source === 'none') {
    return (
      <span className="text-[9px] px-1.5 py-px rounded bg-amber-500/10 text-amber-300/80 uppercase tracking-wide">
        Not configured
      </span>
    );
  }
  if (source === 'static') {
    // 'static' = the bundled DEFAULT_BASE_URLS fallback fired because
    // the scraper hasn't yet heartbeated with a default_base_url
    // (fresh deploy, registry doc wiped). Call it out so operators
    // know the scraper itself hasn't confirmed this URL.
    return (
      <span
        className="text-[9px] px-1.5 py-px rounded bg-slate-800 text-slate-400 uppercase tracking-wide"
        title="Bundled fallback — scraper hasn't reported its default yet"
      >
        Default (bundled)
      </span>
    );
  }
  return (
    <span className="text-[9px] px-1.5 py-px rounded bg-slate-800 text-slate-400 uppercase tracking-wide">
      Default (from env)
    </span>
  );
}

/** Best-effort URL parser for the header summary. The override is
 *  free-form so we tolerate templates like `https://x.tld/s?k={term}`
 *  by yanking the host out via the URL constructor. Returns null on
 *  anything that doesn't parse — header just hides the chunk. */
function safeHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
